<template>
  <PageContainer title="Buscar aulas práticas">
    <div v-if="!loaded" class="d-flex justify-content-center">
      <component
        :animation-duration="1000"
        :is="'SpringSpinner'"
        :color="'#1C2D5A'"
        :size="80"
        class="mx-2"
      >
      </component>
    </div>

    <Box
      v-else
      class="aulas-container"
      space="medium"
      style="display: flex; flex-direction: column"
    >
      <form
        @submit.prevent="handleSubmit"
        class="form-container"
        style="display: flex; flex-direction: column"
      >
        <div class="inputs-container" style="display: flex; flex-direction: column">
          <div class="inputs-row" style="display: flex">
            <Select
              class="input-full"
              :filtered="true"
              :options="alunos"
              legend="Aluno"
              placeholder="Digite o nome do aluno"
              :modelValue="aluno"
              @update:modelValue="$event => (this.aluno = $event)"
              :disabled="loading"
            />

            <Select
              class="input-full"
              :options="instrutores"
              legend="Instrutor(a)"
              placeholder="Selecione um instrutor"
              :modelValue="instrutor"
              @update:modelValue="$event => (this.instrutor = $event)"
              :disabled="loading"
            />
          </div>
          <div class="inputs-row" style="display: flex">
            <Select
              class="input-full"
              :options="statusOptions"
              legend="Status da aula"
              placeholder="Selecione um status"
              :modelValue="status"
              @update:modelValue="$event => (this.status = $event)"
              :disabled="loading"
            />

            <Select
              class="input-full"
              :options="veiculos"
              legend="Veículo"
              placeholder="Selecione um veículo"
              :modelValue="veiculo"
              @update:modelValue="$event => (this.veiculo = $event)"
              :disabled="loading"
            />

            <Select
              class="input-full"
              :options="situacoes"
              legend="Situação"
              placeholder="Selecione uma situação"
              :modelValue="situacao"
              @update:modelValue="$event => (situacao = $event)"
              :disabled="loading"
            />
          </div>
          <div class="inputs-row" style="display: flex">
            <Period
              legend="Data"
              :firstModelValue="initialDate"
              :secondModelValue="finalDate"
              @update:firstModelValue="$event => (this.initialDate = $event)"
              @update:secondModelValue="$event => (this.finalDate = $event)"
              :disabled="loading"
            />

            <div class="form-buttons" style="display: flex">
              <Button type="submit" text="Consultar" color="primary" :disabled="loading">
                <Magnify :size="18" />
              </Button>

              <Button
                v-if="!loading && aulas.length > 0"
                type="button"
                @handle:click="handleExport"
                text="Exportar"
                :fill="false"
                color="primary"
                :disabled="loading"
              >
                <TrayArrowDown :size="18" />
              </Button>
            </div>
          </div>
        </div>
      </form>

      <div v-if="loading" class="d-flex justify-content-center">
        <component
          :animation-duration="1000"
          :is="'SpringSpinner'"
          :color="'#1C2D5A'"
          :size="80"
          class="mx-2"
        >
        </component>
      </div>

      <template v-if="!loading && aulas.length > 0">
        <div class="table-container">
          <Paginate
            :total-pages="paginationData.lastPage"
            :current-page="paginationData.currentPage"
            @page-changed="fetchAulas"
          />

          <!-- Barra de ações em lote - Design melhorado -->
          <transition name="slide-down">
            <div v-if="aulasSelecionadas.length > 0" class="bulk-actions-bar" data-testid="barra_acoes_lote">
              <div class="selection-info">
                <div class="selection-icon">
                  <TrashCanOutline :size="20" />
                </div>
                <div class="selection-text">
                  <span class="count" data-testid="contador_selecoes">{{ aulasSelecionadas.length }}</span>
                  <span class="label">{{ aulasSelecionadas.length === 1 ? 'aula selecionada' : 'aulas selecionadas' }}</span>
                </div>
              </div>
              
              <div class="bulk-actions">
                <Button 
                  type="button" 
                  @handle:click="limparSelecoes" 
                  text="Limpar" 
                  color="secondary"
                  size="small"
                  :disabled="loading"
                  data-testid="botao_limpar_selecoes"
                />
                <Button 
                  type="button" 
                  @handle:click="abrirModalCancelamentoLote" 
                  text="Cancelar Selecionadas" 
                  color="danger"
                  :disabled="loading || loadingCancelamentoLote"
                  :loading="loadingCancelamentoLote"
                  data-testid="botao_cancelar_selecionadas"
                >
                  <TrashCanOutline :size="16" />
                </Button>
              </div>
            </div>
          </transition>

          <!-- Tabela customizada com checkboxes -->
          <div class="table-container" data-testid="tabela_aulas_praticas">
            <table class="vuestic-table">
              <thead>
                <tr>
                  <th v-if="!isFiltrandoCanceladas" style="width: 40px; text-align: center;">
                    <label data-testid="checkbox_selecionar_todas">
                      <input 
                        type="checkbox" 
                        :checked="aulasDisponiveis.length > 0 && aulasSelecionadas.length === aulasDisponiveis.length"
                        :indeterminate.prop="aulasSelecionadas.length > 0 && aulasSelecionadas.length < aulasDisponiveis.length"
                        @change="selecionarTodas($event.target.checked)"
                        :disabled="aulasDisponiveis.length === 0"
                      />
                      <span></span>
                    </label>
                  </th>
                  <template v-if="isFiltrandoCanceladas">
                    <th>Aluno</th>
                    <th>Instrutor</th>
                    <th>Veículo</th>
                    <th>Status</th>
                    <th>Justificativa</th>
                    <th>Cancelada por</th>
                    <th>Data e hora</th>
                    <th>Ações</th>
                  </template>
                  <template v-else>
                    <th>Aluno</th>
                    <th>Instrutor</th>
                    <th>Veículo</th>
                    <th>Observação</th>
                    <th>Data e hora</th>
                    <th>Status</th>
                    <th>Conteúdos</th>
                    <th>Ações</th>
                  </template>
                </tr>
              </thead>
              <tbody>
            <tr v-for="aula in aulas" :key="aula.id_aulas_praticas" :class="{ 'selected': aulasSelecionadas.includes(aula.id_aulas_praticas) }" :data-testid="`linha_aula_${aula.id_aulas_praticas}`">
              <!-- Checkbox - não aparece se estiver filtrando canceladas -->
              <td v-if="!isFiltrandoCanceladas && podeSelecionar(aula)" class="checkbox">
                <label>
                  <input 
                    type="checkbox" 
                    :checked="aulasSelecionadas.includes(aula.id_aulas_praticas)"
                    @change="alternarSelecao(aula.id_aulas_praticas)"
                    :data-testid="`checkbox_aula_${aula.id_aulas_praticas}`"
                  />
                  <span></span>
                </label>
              </td>
              <td v-else-if="!isFiltrandoCanceladas"></td>
              
              <!-- Layout específico para aulas canceladas -->
              <template v-if="isFiltrandoCanceladas">
                <td>{{ aula.aluno.nome }}</td>
                <td>{{ aula.instrutor.nome }}</td>
                <td>{{ `Categoria ${aula.cnh} / ${aula.veiculo.placa}` }}</td>
                <td>Cancelada</td>
                <td>
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <span v-if="!aula.observacao || aula.observacao.length === 0">-</span>
                    <template v-else>
                      <span style="max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                        {{ aula.observacao }}
                      </span>
                      <IconButton
                        @handle:click="abrirModalJustificativa(aula.observacao)"
                        color="primary"
                        title="Ver justificativa completa"
                        :disabled="loadingActions"
                        style="min-width: auto;"
                      >
                        <FileDocumentOutline :size="16" />
                      </IconButton>
                    </template>
                  </div>
                </td>
                <td>{{ aula.email_cancelador || '-' }}</td>
                <td>{{ utils.formatDate(aula.data_aula) }}</td>
                <td>
                  <div class="actions" style="display: flex">
                    <div style="display: flex">
                      <IconButton
                        @handle:click="detalhesAulaCancelada(aula.id_aulas_praticas)"
                        color="secondary"
                        title="Detalhes"
                        :disabled="loadingActions"
                      >
                        <InformationOutline :size="19" />
                      </IconButton>
                    </div>
                  </div>
                </td>
              </template>
              
              <!-- Layout normal para aulas não canceladas -->
              <template v-else>
                <td>{{ aula.aluno.nome }}</td>
                <td>{{ aula.instrutor.nome }}</td>
                <td>{{ `Categoria ${aula.cnh} / ${aula.veiculo.placa}` }}</td>
                <td>
                  {{
                    aula.observacao &&
                    (aula.observacao.indexOf('Tempo de aula: ') != -1 ||
                      aula.observacao.indexOf('Aula dupla não possuiu tempo mínimo') != -1)
                      ? aula.observacao
                      : '-'
                  }}
                </td>
                <td>{{ utils.formatDate(aula.data_aula) }}</td>
                <td>
                  <span v-if="!aula.status">
                    <span v-if="aula.data_aula > data_atual && aula.cancelada == false">
                      Aula não realizada
                    </span>
                    <span
                      v-else-if="
                        aula.data_aula < data_atual &&
                        aula.cancelada == false &&
                        !!aula.id_anotacao_pratica
                      "
                    >
                      Aula não enviada
                    </span>
                    <span
                      v-else-if="
                        aula.data_aula < data_atual &&
                        aula.cancelada == false &&
                        !!!aula.id_anotacao_pratica
                      "
                    >
                      Falta
                    </span>
                    <span v-else-if="aula.cancelada == true">Aula cancelada</span>
                  </span>
                  <span v-else>
                    <span v-if="aula.cancelada == true">Aula cancelada</span>
                    <span v-else>{{ aula.status }}</span>
                  </span>
                </td>
                <td>{{ aula.conteudos[0] || '-' }}</td>
                <td v-if="aula.id_anotacao_pratica">
                  <div class="actions">
                    <div>
                      <IconButton
                        @handle:click="openInfoModal(aula.id_aulas_praticas)"
                        color="secondary"
                        title="Detalhes"
                        :disabled="loadingActions"
                      >
                        <InformationOutline :size="19" />
                      </IconButton>
                    </div>
                    <div>
                      <IconButton
                        @handle:click="openMap(aula.id_aulas_praticas)"
                        color="primary"
                        title="Mapa"
                        :disabled="loadingActions"
                      >
                        <MapOutline :size="19" />
                      </IconButton>
                    </div>
                  </div>
                </td>
                <td v-else>
                  <div class="actions" style="display: flex">
                    <div style="display: flex">
                      <IconButton
                        @handle:click="detalhesAulaPraticaAgendada(aula.id_aulas_praticas)"
                        color="secondary"
                        title="Detalhes"
                        :disabled="loadingActions"
                      >
                        <InformationOutline :size="19" />
                      </IconButton>
                    </div>
                    <div v-if="[1, 5].includes(parseInt(id_role))" style="display: flex">
                      <IconButton
                        @handle:click="justificativaModal(aula.id_aulas_praticas)"
                        color="danger"
                        title="Remover aula"
                        :disabled="loadingActions"
                      >
                        <TrashCanOutline :size="19" />
                      </IconButton>
                    </div>
                  </div>
                </td>
              </template>
            </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </Box>

    <ExportModal
      ref="exportModal"
      type="reduced"
      @submit:export="submitExport"
      :loading="exportLoading"
    />

    <MapModal ref="mapModal" />

    <InfoModal ref="infoModal" :schema="schema" @refetch="fetchAulas" />

    <!-- Modal de cancelamento em lote - Design melhorado -->
    <vuestic-modal 
      ref="cancelamentoLoteModal" 
      cancelClass="none" 
      okClass="none"
      :large="true"
      data-testid="modal_cancelamento_lote"
    >
      <div slot="title">
        <div style="padding: 48px 0 24px 0;" data-testid="header_cancelamento_lote">
          <h3 style="margin: 0; font-size: 1.5rem; font-weight: 600; color: #1C2D5A;">Cancelar Aulas em Lote</h3>
          <p style="margin: 12px 0 0 0; font-size: 1rem; color: #6b7280;">Esta ação não pode ser desfeita</p>
        </div>
      </div>
      
      <div style="padding: 20px;" data-testid="conteudo_modal_cancelamento">
        <!-- Card de informações das aulas -->
        <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; margin-bottom: 24px;" data-testid="card_informacoes_aulas">
          <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px;">
            <div style="padding: 8px; background: #dbeafe; border-radius: 8px; color: #2563eb;">
              <InformationOutline :size="20" />
            </div>
            <div>
              <div style="font-size: 1.125rem; font-weight: 600; color: #1f2937;" data-testid="contador_aulas_selecionadas">
                {{ aulasSelecionadas.length }}
              </div>
              <div style="font-size: 0.875rem; color: #6b7280;">
                {{ aulasSelecionadas.length === 1 ? 'aula será cancelada' : 'aulas serão canceladas' }}
              </div>
            </div>
          </div>
          
          <!-- Lista de aulas que serão canceladas -->
          <div v-if="aulasSelecionadas.length > 0" style="max-height: 200px; overflow-y: auto;">
            <div 
              v-for="aula in aulasSelecionadasDetalhes" 
              :key="aula.id_aulas_praticas"
              style="display: flex; justify-content: space-between; align-items: center; padding: 12px; background: white; border-radius: 8px; margin-bottom: 8px; border: 1px solid #e5e7eb;"
            >
              <div>
                <div style="font-weight: 500; color: #1f2937;">{{ aula.aluno.nome }}</div>
                <div style="font-size: 0.875rem; color: #6b7280;">{{ utils.formatDate(aula.data_aula) }}</div>
              </div>
              <div>
                <span style="padding: 4px 12px; background: #FFC107; color: #1C2D5A; border-radius: 6px; font-size: 0.75rem; font-weight: 600;">
                  {{ aula.status || 'Falta' }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Campo de justificativa -->
        <div>
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
            <label style="font-weight: 600; color: #1f2937;">Justificativa *</label>
            <span style="font-size: 0.75rem; color: #1C2D5A; background: #FFC107; padding: 4px 8px; border-radius: 4px; font-weight: 600;">
              Obrigatório
            </span>
          </div>
          <div style="position: relative;">
            <textarea
              v-model="justificativaCancelamento"
              rows="4"
              style="width: 100%; border: 2px solid #e5e7eb; border-radius: 8px; padding: 12px; font-family: inherit; font-size: 0.875rem; resize: vertical; min-height: 100px; transition: border-color 0.2s ease;"
              placeholder="Digite o motivo do cancelamento das aulas selecionadas..."
              maxlength="500"
              :disabled="loadingCancelamentoLote"
              @focus="$event.target.style.borderColor = '#3b82f6'; $event.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'"
              @blur="$event.target.style.borderColor = '#e5e7eb'; $event.target.style.boxShadow = 'none'"
              data-testid="textarea_justificativa_cancelamento"
            ></textarea>
            <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 8px;">
              <span style="font-size: 0.75rem; color: #6b7280;" :style="{ color: justificativaCancelamento.length >= 450 ? '#dc2626' : '#6b7280', fontWeight: justificativaCancelamento.length >= 450 ? '500' : 'normal' }">
                {{ justificativaCancelamento.length }}/500 caracteres
              </span>
              <div v-if="justificativaCancelamento.length >= 3" style="color: #10b981; font-weight: bold;">
                ✓
              </div>
            </div>
          </div>
          <div style="font-size: 1rem; color: #6b7280; margin-top: 8px;">
            Descreva o motivo do cancelamento. Esta informação ficará registrada no histórico.
          </div>
        </div>
      </div>
      
      <div style="padding: 16px 24px; border-top: 1px solid #e5e7eb; display: flex; justify-content: flex-end; gap: 12px; background: #f9fafb;" data-testid="footer_modal_cancelamento">
        <button 
          type="button" 
          @click="fecharModalCancelamentoLote"
          :disabled="loadingCancelamentoLote"
          style="padding: 8px 16px; border: 1px solid #d1d5db; background: white; color: #374151; border-radius: 6px; font-weight: 500; cursor: pointer;"
          :style="{ opacity: loadingCancelamentoLote ? '0.5' : '1', cursor: loadingCancelamentoLote ? 'not-allowed' : 'pointer' }"
          data-testid="botao_cancelar_modal"
        >
          Cancelar
        </button>
        <button 
          type="button" 
          @click="confirmarCancelamentoLote"
          :disabled="!isJustificativaValida || loadingCancelamentoLote"
          style="padding: 8px 16px; border: none; background: #dc2626; color: white; border-radius: 6px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 8px;"
          :style="{ 
            opacity: (!isJustificativaValida || loadingCancelamentoLote) ? '0.5' : '1', 
            cursor: (!isJustificativaValida || loadingCancelamentoLote) ? 'not-allowed' : 'pointer',
            background: loadingCancelamentoLote ? '#9ca3af' : '#dc2626'
          }"
          data-testid="botao_confirmar_cancelamento"
        >
          <span v-if="loadingCancelamentoLote" style="display: inline-block; width: 16px; height: 16px; border: 2px solid transparent; border-top: 2px solid white; border-radius: 50%; animation: spin 1s linear infinite;"></span>
          <TrashCanOutline :size="16" v-if="!loadingCancelamentoLote" />
          {{ loadingCancelamentoLote ? 'Cancelando...' : 'Confirmar Cancelamento' }}
        </button>
      </div>
    </vuestic-modal>

    <!-- Modal de justificativa completa -->
    <vuestic-modal
      ref="modalJustificativaCompleta"
      cancelClass="none"
      okClass="none"
      :small="false"
      data-testid="modal_justificativa_completa"
    >
      <div slot="title">
        <div style="display: flex; align-items: center; gap: 12px; padding: 16px 0 8px 0;">
          <div style="width: 48px; height: 48px; background: #1C2D5A; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
            <FileDocumentOutline :size="24" style="color: #ffffff;" />
          </div>
          <div style="flex: 1;">
            <h3 style="margin: 0; font-size: 1.25rem; font-weight: 600; color: #1f2937;">
              Justificativa do Cancelamento
            </h3>
            <p style="margin: 2px 0 0 0; font-size: 0.875rem; color: #6b7280;">
              Visualização completa da justificativa
            </p>
          </div>
        </div>
      </div>
      
      <div style="padding: 20px 24px;">
        <!-- Card da justificativa -->
        <div style="background: #ffffff; border: 1px solid #e5e7eb; border-left: 4px solid #FFC107; border-radius: 8px; padding: 16px; min-height: 80px; max-height: 300px; overflow-y: auto;">
          <p style="margin: 0; color: #374151; font-size: 0.9375rem; line-height: 1.6; white-space: pre-wrap; word-break: break-word;">
            {{ justificativaCompleta }}
          </p>
        </div>
        
        <!-- Mensagem informativa -->
        <div style="margin-top: 16px; padding: 12px 14px; background: #eff6ff; border-radius: 6px; display: flex; align-items: start; gap: 10px;">
          <InformationOutline :size="18" style="color: #2563eb; flex-shrink: 0; margin-top: 1px;" />
          <p style="margin: 0; font-size: 0.8125rem; color: #1e40af; line-height: 1.5;">
            Esta justificativa foi registrada no momento do cancelamento e ficará permanentemente armazenada no histórico.
          </p>
        </div>
      </div>
      
      <div style="padding: 14px 24px; border-top: 1px solid #e5e7eb; background: #f9fafb; display: flex; justify-content: flex-end;">
        <button
          type="button"
          @click="$refs.modalJustificativaCompleta.close()"
          style="padding: 10px 24px; border: none; background: #1C2D5A; color: white; border-radius: 6px; font-weight: 500; font-size: 0.9375rem; cursor: pointer; transition: all 0.2s ease;"
          onmouseover="this.style.background='#2d4a7c';"
          onmouseout="this.style.background='#1C2D5A';"
        >
          Fechar
        </button>
      </div>
    </vuestic-modal>

    <vuestic-modal
      :show.sync="modal.show"
      ref="editModal"
      v-bind:small="false"
      cancelClass="none"
      okClass="none"
    >
      <vuestic-tabs :names="['Editar Aula']" ref="tabs">
        <div slot="Editar Aula">
          <div class="container" v-if="aulaEdit != []">
            <div class="row">
              <div class="col-md-4">
                <strong class="font-weight-bold">Aluno:</strong>
                <p v-if="aulaEdit.aluno">{{ aulaEdit.aluno.nome }}</p>
              </div>
              <div class="col-md-4">
                <strong class="font-weight-bold">Instrutor:</strong>
                <p v-if="aulaEdit.aluno">{{ aulaEdit.instrutor.nome }}</p>
              </div>
              <div class="col-md-4">
                <strong class="font-weight-bold">Categoria:</strong>
                <p v-if="aulaEdit.aluno">{{ aulaEdit.cnh }}</p>
              </div>
            </div>
            <br />
            <div class="row">
              <div class="col-md-2">
                <strong class="font-weight-bold">Placa:</strong>
                <p v-if="aulaEdit.veiculo">{{ aulaEdit.veiculo.placa }}</p>
              </div>
              <div class="col-md-2">
                <strong class="font-weight-bold">Tipo:</strong>
                <p v-if="aulaEdit.tipo_aula">{{ aulaEdit.tipo_aula }}</p>
              </div>
              <div class="col-md-4">
                <strong class="font-weight-bold">Inicio da Aula:</strong>
                <p v-if="aulaEdit.data_aula">{{ utils.formatDate(aulaEdit.data_aula) }}</p>
              </div>
              <div class="col-md-4">
                <strong class="font-weight-bold">Duração:</strong>
                <p>50 Minutos</p>
              </div>
            </div>
          </div>
          <hr />
          <form id="form_editar_aula_pratica">
            <div class="row">
              <div class="col-md-12">
                <DateIconGroup
                  idInput="data_aula"
                  iconInput="fa-calendar"
                  labelInput="Data da Aula"
                  :enableTime="true"
                >
                </DateIconGroup>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <SelectIconGroup
                  iconInput="fa-user"
                  idInput="id_instrutor"
                  labelInput="Instrutor"
                  :optionsInput="optionsInputIstrutor"
                >
                </SelectIconGroup>
              </div>
              <div class="col-md-6">
                <SelectIconGroup
                  iconInput="fa-car"
                  idInput="id_veiculo"
                  labelInput="Veículo"
                  :optionsInput="optionsInputVeiculo"
                >
                </SelectIconGroup>
              </div>
            </div>
          </form>
          <div class="row justify-content-center">
            <button class="btn btn-primary" @click="editSave()">Salvar</button>
          </div>
        </div>
      </vuestic-tabs>
    </vuestic-modal>

    <vuestic-modal
      :large="true"
      :show.sync="modal.show"
      ref="modalRealizadas"
      v-bind:small="false"
      :cancelClass="'none'"
      okText="Fechar"
      cancelText="Fechar"
    >
      <div slot="title">Informações</div>
      <div v-show="modal.load">
        <div class="col-sm-12 col-lg-12 col-xl-12 d-flex justify-content-center">
          <div
            class="btn-toolbar d-inline-flex"
            role="toolbar"
            aria-label="Toolbar with button groups"
          >
            <component
              :animation-duration="1000"
              :is="'SpringSpinner'"
              :color="'#4AE387'"
              :size="80"
            ></component>
          </div>
        </div>
      </div>
      <div v-if="modal.show">
        <vuestic-tabs
          @change="ativar($event)"
          :names="['Dados Básicos', 'Avaliações', 'Feedback']"
          ref="tabs"
        >
          <!-- #Início# Dados Básicos -->
          <div slot="Dados Básicos">
            <div class="row">
              <div class="col-md-12 text-center">
                <h3 class="font-weight-bold">Centro de Formação FabricaInfo</h3>
                <hr />
              </div>
              <div class="col-md-2">
                <strong class="font-weight-bold">Aluno:</strong>
                <p>{{ modal.conteudo.nomeAluno }}</p>
                <span style="font-size: small; font-weight: bold">{{
                  modal.conteudo.cpfAluno
                }}</span>
              </div>
              <div class="col-md-2">
                <strong class="font-weight-bold">Instrutor:</strong>
                <p>{{ modal.conteudo.nomeInstrutor }}</p>
                <span style="font-size: small; font-weight: bold">{{
                  modal.conteudo.cpfInstrutor
                }}</span>
              </div>
              <div class="col-md-2">
                <strong class="font-weight-bold">Categoria:</strong>
                <p>{{ modal.conteudo.categoria_cnh }}</p>
              </div>
              <div class="col-md-2">
                <strong class="font-weight-bold">Tipo:</strong>
                <p>{{ modal.conteudo.tipo_aula }}</p>
              </div>
              <div class="col-md-4">
                <strong class="font-weight-bold">Conteúdo:</strong>
                <p>{{ modal.conteudo.anotacao.edu_content }}</p>
              </div>
            </div>
            <br />
            <div class="row">
              <div class="col-md-3">
                <strong class="font-weight-bold">Modelo:</strong>
                <p>{{ modal.conteudo.modelo }}</p>
              </div>
              <div class="col-md-3">
                <strong class="font-weight-bold">Marca:</strong>
                <p>{{ modal.conteudo.marca }}</p>
              </div>
              <div class="col-md-2">
                <strong class="font-weight-bold">Cor:</strong>
                <p>{{ modal.conteudo.cor }}</p>
              </div>
              <div class="col-md-2">
                <strong class="font-weight-bold">Ano:</strong>
                <p>{{ modal.conteudo.ano_modelo }}</p>
              </div>
              <div class="col-md-2">
                <strong class="font-weight-bold">Placa:</strong>
                <p>{{ modal.conteudo.placa }}</p>
              </div>
            </div>
            <br />
            <div class="row">
              <div class="col-md-4">
                <strong class="font-weight-bold">Início da Aula (Previsto):</strong>
                <p>{{ utils.formatDate(modal.conteudo.data_aula) }}</p>
              </div>
              <div class="col-md-4">
                <strong class="font-weight-bold">Duração (Previsto):</strong>
                <p>50 Minutos</p>
              </div>
              <div class="col-md-4">
                <strong class="font-weight-bold">Fim da aula (Previsto):</strong>
                <p>
                  {{
                    utils.formatDate(
                      modal.conteudo.data_aula.split(' ')[0] +
                        ' ' +
                        new Date(
                          new Date(modal.conteudo.data_aula).getTime() + 50 * 60000
                        ).toLocaleTimeString()
                    )
                  }}
                </p>
              </div>
            </div>
            <br />
            <div class="row">
              <div class="col-md-8">
                <strong class="font-weight-bold">Início da Aula (Real):</strong>
                <p>{{ utils.formatDate(modal.conteudo.anotacao.begin_time) }}</p>
              </div>
              <div class="col-md-4">
                <strong class="font-weight-bold">Fim da aula (Real):</strong>
                <p>{{ utils.formatDate(modal.conteudo.anotacao.finish_time) }}</p>
              </div>
            </div>
            <br />
            <div class="row">
              <div class="col-md-2">
                <strong class="font-weight-bold">Quilometragem inicial:</strong>
                <p>{{ modal.conteudo.anotacao.hodo_start }}&nbsp;&nbsp;Km</p>
              </div>
              <div class="col-md-2">
                <strong class="font-weight-bold">Quilometragem final:</strong>
                <p>{{ modal.conteudo.anotacao.hodo_end }}&nbsp;&nbsp;Km</p>
              </div>
              <div class="col-md-4">
                <strong class="font-weight-bold">Distância percorrida (Hodômetro):</strong>
                <p>
                  {{
                    modal.conteudo.anotacao.hodo_end - modal.conteudo.anotacao.hodo_start - 1
                  }}&nbsp;&nbsp;Km
                </p>
              </div>
              <div id="distance_gps" class="col-md-4">
                <strong class="font-weight-bold">Distância percorrida (GPS):</strong>
                <p>&nbsp;&nbsp;</p>
              </div>
            </div>
            <br />
            <!-- <div class="row">
							<div class="col-md-6 text-center">
								<strong class="font-weight-bold">Aluno Presente?</strong>
								<br>
								<span v-if="modal.conteudo.aluno_presente">SIM</span>
								<span v-else>NÃO</span>
							</div>
							<div class="col-md-6 text-center">
								<strong class="font-weight-bold">Instrutor Presente?</strong>
								<br>
								<span v-if="modal.conteudo.instrutor_presente">SIM</span>
								<span v-else>NÃO</span>
							</div>
						</div> -->
            <br />
            <div v-if="modal.conteudo.anotacao.finish_reason != null" class="row">
              <div class="col-md-12 text-center">
                <strong class="font-weight-bold" style="color: red">AULA NÃO CONCLUÍDA</strong>
                <br />
              </div>
              <div class="col-md-12 text-center">
                <strong class="font-weight-bold">Justificativa:</strong>
                <p>{{ modal.conteudo.anotacao.finish_reason }}</p>
              </div>
            </div>
            <br />
            <div v-if="modal.conteudo.dupla" class="row">
              <div class="col-md-12 text-center">
                <strong class="font-weight-bold" style="color: blue">AULA DUPLA</strong>
                <br />
              </div>
            </div>
            <div class="row">
              <button
                v-if="!modal.conteudo.anotacao.processada"
                title="Processar Aula"
                class="btn btn-info size"
                @click="processarAnotacao(modal.conteudo.anotacao.id_anotacao_pratica)"
              >
                Processar Aula
              </button>
            </div>
          </div>
          <!-- #Fim# Dados Básicos -->

          <!-- #Início# Avaliação -->
          <div slot="Avaliações">
            <div v-if="avaliacoes">
              <AvaliacoesTab :aulaId="aulaId"></AvaliacoesTab>
            </div>
          </div>
          <!-- #Fim# Avaliação -->

          <!-- #Início# Feedback -->
          <div slot="Feedback">
            <div v-if="feedback">
              <FeedbackTab :aulaId="aulaId"></FeedbackTab>
            </div>
          </div>
          <!-- #Fim# Feedback -->

          <!-- #Início# Mapa -->
          <!-- <div slot="Mapa">
            <div class="row">
              <div id="mapa" class="col-md-12 text-center justify-content-center">
                <GoogleMap
                  :infractions="modal.conteudo.anotacao.infracoes"
                  :positions="modal.conteudo.anotacao.posicoes"
                  :beginTime="modal.conteudo.data_aula"
                />
              </div>
            </div>
          </div> -->
          <!-- #Fim# Mapa -->

          <!-- #Início# Fotos -->
          <div slot="Fotos">
            <div class="row">
              <div class="col-md-6" style="height: 400px; overflow: scroll">
                <span
                  v-for="(foto, fotoIndex) in modal.conteudo.anotacao.fotos"
                  :key="'foto_' + foto.id_foto_pratica"
                >
                  <img
                    v-if="foto.id_status === 1"
                    :id="'status_' + foto.id_foto_pratica"
                    @click="checkImage(fotoIndex)"
                    class="img-thumb"
                    :src="'data:image/' + foto.ext + ';base64, ' + foto.foto"
                    alt="Imagem"
                    style="border: solid 5px rgb(255, 0, 0)"
                  />

                  <img
                    v-else-if="foto.id_status === 2"
                    :id="'status_' + foto.id_foto_pratica"
                    @click="checkImage(fotoIndex)"
                    class="img-thumb"
                    :src="'data:image/' + foto.ext + ';base64, ' + foto.foto"
                    alt="Imagem"
                    style="border: solid 5px rgb(255, 0, 0)"
                  />

                  <img
                    v-else-if="foto.id_status === 3"
                    :id="'status_' + foto.id_foto_pratica"
                    @click="checkImage(fotoIndex)"
                    class="img-thumb"
                    :src="'data:image/' + foto.ext + ';base64, ' + foto.foto"
                    alt="Imagem"
                    style="border: solid 5px rgb(255, 251, 0)"
                  />

                  <img
                    v-else-if="foto.id_status === 4"
                    :id="'status_' + foto.id_foto_pratica"
                    @click="checkImage(fotoIndex)"
                    class="img-thumb"
                    :src="'data:image/' + foto.ext + ';base64, ' + foto.foto"
                    alt="Imagem"
                    style="border: solid 5px rgb(9, 255, 0)"
                  />

                  <img
                    v-else
                    @click="checkImage(fotoIndex)"
                    :id="'status_' + foto.id_foto_pratica"
                    class="img-thumb"
                    :src="'data:image/' + foto.ext + ';base64, ' + foto.foto"
                    alt="Imagem"
                  />
                </span>
              </div>
              <div class="col-md-6">
                <div class="row" style="justify-content: center">
                  <img
                    id="canvas"
                    class="img-monit-modal"
                    alt="Imagem"
                    style="width: 300px; height: 250px"
                  />
                  <div class="container">
                    <div class="row" style="justify-content: center">
                      <table v-if="modal.load" class="table-responsive-sm">
                        <tr>
                          <td>{{ modal.conteudo.anotacao }}</td>
                        </tr>
                      </table>
                    </div>
                    <br />
                    <div class="row">
                      <div class="col-md-12 text-center justify-content-center">
                        <button
                          title="Nenhum aluno detectado"
                          class="btn btn-danger btn-with-icon rounded-icon size"
                          @click="imageStatus(modal.img, 2)"
                        >
                          <span
                            aria-hidden="true"
                            class="fa fa-user-times"
                            style="font-size: 16px; padding-left: 6px"
                          ></span>
                        </button>
                        &nbsp;
                        <button
                          title="Sem imagem"
                          class="btn btn-danger btn-with-icon rounded-icon size"
                          @click="imageStatus(modal.img, 1)"
                        >
                          <span
                            aria-hidden="true"
                            class="fa fa-low-vision"
                            style="font-size: 16px; padding-left: 2px"
                          ></span>
                        </button>
                        &nbsp;
                        <button
                          title="Imagem com erro"
                          class="btn btn-warning btn-with-icon rounded-icon size"
                          @click="imageStatus(modal.img, 3)"
                        >
                          <span
                            aria-hidden="true"
                            class="fa fa-exclamation-triangle"
                            style="font-size: 16px; padding-left: 3px"
                          ></span>
                        </button>
                        &nbsp;
                        <button
                          title="Imagem OK"
                          class="btn btn-success btn-with-icon rounded-icon size"
                          @click="imageStatus(modal.img, 4)"
                        >
                          <span
                            aria-hidden="true"
                            class="fa fa-check"
                            style="font-size: 16px; padding-left: 3px"
                          ></span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <span>{{ modal.conteudo.anotacao.fotos.length }}</span>
              </div>
              <div class="col-md-6">
                <button
                  title="Validar todas"
                  class="btn btn-success btn-with-icon rounded-icon size"
                  @click="validarTodas(modal.conteudo.anotacao.fotos)"
                >
                  <span
                    aria-hidden="true"
                    class="fa fa-check"
                    style="font-size: 16px; padding-left: 3px"
                  ></span>
                </button>
              </div>
            </div>
          </div>
          <!-- #Fim# Fotos -->
        </vuestic-tabs>
      </div>
    </vuestic-modal>

    <vuestic-modal
      :show.sync="modalAulaPraticaAgendada.show"
      ref="smallModal"
      v-bind:small="false"
      :cancelClass="'none'"
      okText="Fechar"
      cancelText="Fechar"
    >
      <div slot="title">Informações</div>
      <div v-show="modalAulaPraticaAgendada.load">
        <div class="col-sm-12 col-lg-12 col-xl-12 d-flex justify-content-center">
          <div
            class="btn-toolbar d-inline-flex"
            role="toolbar"
            aria-label="Toolbar with button groups"
          >
            <component
              :animation-duration="1000"
              :is="'SpringSpinner'"
              :color="'#4AE387'"
              :size="80"
            ></component>
          </div>
        </div>
      </div>
      <div v-show="!modalAulaPraticaAgendada.load">
        <vuestic-tabs :names="['Dados Básicos']" ref="tabs">
          <div slot="Dados Básicos">
            <div class="row">
              <div class="col-md-4 dados">
                <strong class="font-weight-bold">Aluno:</strong>
                <p>{{ modalAulaPraticaAgendada.conteudo.nomeAluno }}</p>
              </div>
              <div class="col-md-4 dados">
                <strong class="font-weight-bold">Instrutor:</strong>
                <p>{{ modalAulaPraticaAgendada.conteudo.nomeInstrutor }}</p>
              </div>
              <div class="col-md-2 dados">
                <strong class="font-weight-bold">Categoria:</strong>
                <p>{{ modalAulaPraticaAgendada.conteudo.categoria_cnh }}</p>
              </div>
              <div class="col-md-2 dados">
                <strong class="font-weight-bold">Placa:</strong>
                <p>{{ modalAulaPraticaAgendada.conteudo.placa }}</p>
              </div>
            </div>
            <br />
            <div class="row">
              <div class="col-md-4 dados">
                <strong class="font-weight-bold">Tipo:</strong>
                <p>{{ modalAulaPraticaAgendada.conteudo.tipo_aula }}</p>
              </div>
              <div class="col-md-4 dados">
                <strong class="font-weight-bold">Inicio da Aula:</strong>
                <p>{{ utils.formatDate(modalAulaPraticaAgendada.conteudo.data_aula) }}</p>
              </div>
              <div class="col-md-4 dados">
                <strong class="font-weight-bold">Duração:</strong>
                <p>50 Minutos</p>
              </div>
            </div>
          </div>
        </vuestic-tabs>
      </div>
    </vuestic-modal>

    <vuestic-modal ref="justificativaModal" cancelClass="none" okClass="none">
      <div slot="title">Justificativa do Cancelamento</div>
      <div class="d-flex justify-content-center">
        <textarea
          v-model="justificativa"
          cols="70"
          rows="5"
          placeholder="Escreva o motivo pelo qual quer cancelar o agendamento."
          style="resize: none"
          class="border border-dark p-2"
          maxlength="250"
        ></textarea>
      </div>
      <div class="row justify-content-center mt-4">
        <button
          class="btn btn-primary"
          :disabled="justificativaVazia"
          @click="removerAula(justificativaAulaPratica, justificativa)"
        >
          Enviar
        </button>
      </div>
    </vuestic-modal>
  </PageContainer>
</template>

<script>
import * as spinners from 'epic-spinners'
import InformationOutline from 'icons/InformationOutline'
import Magnify from 'icons/Magnify'
import TrashCanOutline from 'icons/TrashCanOutline'
import TrayArrowDown from 'icons/TrayArrowDown'
import MapOutline from 'icons/MapOutline'
import FileDocumentOutline from 'icons/FileDocumentOutline'
import _export from '../../services/export'
import utils from '../../services/utils'
import Box from '../core/Box'
import Button from '../core/Button'
import IconButton from '../core/IconButton'
import Input from '../core/Input'
import PageContainer from '../core/PageContainer'
import Paginate from '../core/Paginate'
import Period from '../core/Period'
import Select from '../core/Select'
import Table from '../core/Table'
import ExportModal from '../export/ExportModal'
import InfoModal from './InfoModal.vue'

import DateIconGroup from '@/vuestic-theme/vuestic-components/vuestic-input/DateIconGroup'
import SelectIconGroup from '@/vuestic-theme/vuestic-components/vuestic-input/SelectIconGroup'
import urlApi from '../../router/urlApi'
import GoogleMap from '../google/GoogleMap'
import AgendamentoController from './AgendamentoController'
import AvaliacoesTab from './AvaliacoesTab'
import FeedbackTab from './FeedbackTab'
import MapModal from './MapModal'

export default {
  components: {
    ...spinners,

    PageContainer,
    Box,
    Input,
    Select,
    Period,
    Button,
    Table,
    Paginate,
    IconButton,
    Magnify,
    TrayArrowDown,
    InformationOutline,
    TrashCanOutline,
    MapOutline,
    FileDocumentOutline,

    ExportModal,
    InfoModal,

    GoogleMap,
    SelectIconGroup,
    DateIconGroup,
    FeedbackTab,
    AvaliacoesTab,
    MapModal,
  },
  data() {
    return {
      schema: localStorage.schema,
      id_role: localStorage.id_role,
      utils: utils,
      aluno: null,
      instrutor: null,
      veiculo: null,
      status: null,
      tipo: null,
      initialDate: '',
      finalDate: '',
      data_atual: '',
      justificativa: '',
      aulaId: '',
      justificativaAulaPratica: 0,
      paginationData: {
        currentPage: 1,
        lastPage: 0,
        total: 0,
      },
      modal: {
        show: false,
        load: false,
        conteudo: {},
      },
      modalAulaPraticaAgendada: {
        show: false,
        load: false,
        conteudo: {},
        img: 0,
      },
      alunos: [],
      instrutores: [],
      veiculos: [],
      statusOptions: [],
      situacoes: [
        { text: 'Agendada', value: 'agendada' },
        { text: 'Realizada', value: 'realizada' },
        { text: 'Cancelada', value: 'cancelada' },
      ],
      situacao: '',
      aulas: [],
      optionsInputIstrutor: [
        {
          id: '',
          value: 'Selecione um Instrutor',
        },
      ],
      optionsInputVeiculo: [
        {
          id: '',
          value: 'Selecione um Veículo',
        },
      ],
      aulaEdit: [],
      loaded: false,
      loading: false,
      loadingActions: false,
      exportLoading: false,
      justificativaVazia: true,
      avaliacoesCarregando: false,
      feedback: false,
      avaliacoes: false,
      
      // Propriedades para cancelamento em lote
      aulasSelecionadas: [],
      loadingCancelamentoLote: false,
      justificativaCancelamento: '',
      
      // Modal de justificativa
      justificativaCompleta: '',
    }
  },
  beforeCreate() {
    let self = this
    AgendamentoController.getInstrutores(function (data) {
      data.dados.map(function (value) {
        // console.log(value);
        self.optionsInputIstrutor.push({ id: value.id_instrutor, value: value.nome })
      })
    })
    AgendamentoController.getVeiculos(function (data) {
      data.dados.map(function (value) {
        // console.log(value);
        self.optionsInputVeiculo.push({ id: value.id_veiculo, value: value.placa })
      })
    })
  },
  async created() {
    await Promise.all([
      this.loadAlunoCPF(),
      this.loadInstrutores(),
      this.loadStatusAulasPraticas(),
      this.loadVeiculos(),
    ])

    this.loaded = true
  },
  computed: {
    isFiltrandoCanceladas() {
      return this.situacao === 'cancelada';
    },
    aulasDisponiveis() {
      return this.aulas.filter(aula => this.podeSelecionar(aula)).map(aula => aula.id_aulas_praticas);
    },
    aulasSelecionadasDetalhes() {
      return this.aulas.filter(aula => this.aulasSelecionadas.includes(aula.id_aulas_praticas));
    },
    isJustificativaValida() {
      return this.justificativaCancelamento.trim().length >= 3;
    }
  },
  methods: {
    // Métodos para cancelamento em lote
    podeSelecionar(aula) {
      // Aula pode ser selecionada se não estiver cancelada e não tiver anotação prática (não realizada)
      return !aula.cancelada && !aula.id_anotacao_pratica;
    },
    
    selecionarTodas(isChecked) {
      if (isChecked) {
        this.aulasSelecionadas = [...this.aulasDisponiveis];
      } else {
        this.aulasSelecionadas = [];
      }
    },
    
    alternarSelecao(aulaId) {
      const index = this.aulasSelecionadas.indexOf(aulaId);
      if (index > -1) {
        this.aulasSelecionadas.splice(index, 1);
      } else {
        this.aulasSelecionadas.push(aulaId);
      }
    },
    
    limparSelecoes() {
      this.aulasSelecionadas = [];
      this.$toasted.show('Seleções limpas', {
        position: 'top-right',
        type: 'info',
        duration: 2000
      });
    },
    
    validarJustificativa() {
      // Método para feedback em tempo real
    },
    
    abrirModalCancelamentoLote() {
      if (this.aulasSelecionadas.length === 0) {
        this.$toasted.show('Selecione pelo menos uma aula para cancelar!', {
          position: 'top-right',
          type: 'error',
          duration: 4000
        });
        return;
      }
      
      this.justificativaCancelamento = '';
      
      // Abrir modal
      if (this.$refs.cancelamentoLoteModal) {
        this.$refs.cancelamentoLoteModal.open();
      }
    },
    
    fecharModalCancelamentoLote() {
      if (!this.loadingCancelamentoLote) {
        if (this.$refs.cancelamentoLoteModal) {
          this.$refs.cancelamentoLoteModal.close();
        }
        this.justificativaCancelamento = '';
      }
    },
    
    async confirmarCancelamentoLote() {
      // Validações finais
      if (!this.isJustificativaValida) {
        this.$toasted.show('Por favor, forneça uma justificativa válida (mínimo 3 caracteres)', {
          position: 'top-right',
          type: 'error',
          duration: 4000
        });
        return;
      }

      if (this.aulasSelecionadas.length === 0) {
        this.$toasted.show('Nenhuma aula selecionada para cancelamento', {
          position: 'top-right',
          type: 'error'
        });
        return;
      }

      try {
        this.loadingCancelamentoLote = true;
        
        // Toast de início do processo
        this.$toasted.show(`Cancelando ${this.aulasSelecionadas.length} aula(s)...`, {
          position: 'top-right',
          type: 'info',
          duration: 2000
        });
        
        // Chamar a API para cancelar as aulas em lote
        const response = await this.$http.post(`${process.env.VUE_APP_URL_API_V2}/aulas/praticas/cancelar-lote/autoescola/${this.schema}`, {
          aulas_ids: this.aulasSelecionadas,
          justificativa: this.justificativaCancelamento.trim()
        });
        
        // Sucesso
        const quantidadeCancelada = this.aulasSelecionadas.length;
        
        // Parar loading e fechar modal imediatamente
        this.loadingCancelamentoLote = false;
        this.aulasSelecionadas = [];
        
        // Forçar fechamento do modal
        if (this.$refs.cancelamentoLoteModal) {
          this.$refs.cancelamentoLoteModal.close();
        }
        this.justificativaCancelamento = '';
        
        // Recarregar a lista
        await this.fetchAulas();
        
        // Mostrar toast de sucesso
        this.$toasted.show(
          response.body.mensagem || `${quantidadeCancelada} aula(s) cancelada(s) com sucesso!`, 
          {
            position: 'top-right',
            type: 'success',
            duration: 5000,
            action: {
              text: 'OK',
              onClick: (e, toastObject) => {
                toastObject.goAway(0);
              }
            }
          }
        );
        
      } catch (error) {
        console.error('Erro no cancelamento em lote:', error);
        
        let mensagemErro = 'Erro inesperado ao cancelar aulas';
        
        if (error.status === 400) {
          mensagemErro = (error.body && error.body.mensagem) || 'Dados inválidos fornecidos';
        } else if (error.status === 401) {
          mensagemErro = 'Sem permissão para realizar esta operação';
        } else if (error.status === 404) {
          mensagemErro = 'Recurso não encontrado';
        } else if (error.status >= 500) {
          mensagemErro = 'Erro interno do servidor. Tente novamente mais tarde';
        } else if (error.body && error.body.mensagem) {
          mensagemErro = error.body.mensagem;
        }
        
        this.$toasted.show(mensagemErro, {
          position: 'top-right',
          type: 'error',
          duration: 6000,
          action: {
            text: 'Fechar',
            onClick: (e, toastObject) => {
              toastObject.goAway(0);
            }
          }
        });
      } finally {
        this.loadingCancelamentoLote = false;
      }
    },
    async openMap(id) {
      this.loadingActions = true

      await this.$refs.mapModal.open(id)

      this.loadingActions = false
    },
    async loadAlunoCPF() {
      await this.$http.get(`${process.env.VUE_APP_URL_API}/alunos`).then(res => {
        this.alunos = res.data.dados.map(aluno => ({
          value: aluno.id_aluno,
          text: aluno.nome + ' - ' + utils.formatarCPF(aluno.cpf),
        }))
      })
    },
    async loadInstrutores() {
      await this.$http.get(`${process.env.VUE_APP_URL_API}/instrutores`).then(res => {
        this.instrutores = res.data.dados.map(instrutor => ({
          value: instrutor.id_instrutor,
          text: instrutor.nome,
        }))
      })
    },
    async loadStatusAulasPraticas() {
      await this.$http
        .get(`${process.env.VUE_APP_URL_API}/aulas/praticas/status-aulas-praticas`)
        .then(res => {
          this.statusOptions = res.data.dados.status.map(status => ({
            value: status.status,
            text: status.status,
          }))
        })
    },
    async loadVeiculos() {
      await this.$http.get(`${process.env.VUE_APP_URL_API}/veiculos`).then(res => {
        this.veiculos = res.data.dados.map(veiculo => ({
          value: veiculo.id_veiculo,
          text: `Categoria ${veiculo.categoria.categoria_cnh} / ${veiculo.placa}`,
        }))
      })
    },
    async handleSubmit() {
      if (this.validateInputs()) {
        await this.fetchAulas()
      }
    },
    validateInputs() {
      if ((this.initialDate && !this.finalDate) || (!this.initialDate && this.finalDate)) {
        this.$toasted.show('Ambas as datas devem ser fornecidas.', {
          position: 'top-right',
        })

        return false
      }

      if (this.initialDate && this.finalDate) {
        const initialDate = new Date(this.initialDate)
        const finalDate = new Date(this.finalDate)

        if (initialDate > finalDate) {
          this.$toasted.show('A data inicial deve ser menor que a data final.', {
            position: 'top-right',
          })

          return false
        }
      }

      return true
    },
    async fetchAulas(page = 1) {
      this.loading = true

      const params = {
        page,
      }

      await this.$http
        .get(
          `${process.env.VUE_APP_URL_API}/agendamentos/filtro-aulas-praticas/${null}/${
            this.aluno || null
          }/${this.instrutor || null}/${this.veiculo || null}/${null}/${this.initialDate || null}/${
            this.finalDate || null
          }/${null}/${this.tipo || null}/${null}?statusAula=${this.status || null}&situacao=${this.situacao}`,
          {
            params,
          }
        )
        .then(res => {
          const response = res.body.dados

          if (response.aulas_praticas.data.length < 1) {
            this.$toasted.show('Nenhum resultado encontrado!', {
              position: 'top-right',
            })
          }

          this.aulas = response.aulas_praticas.data
          this.data_atual = response.data_atual

          this.paginationData = {
            currentPage: response.aulas_praticas.current_page,
            lastPage: response.aulas_praticas.last_page,
            total: response.aulas_praticas.total,
          }
        })
        .catch(err => {
          this.$toasted.show(err.body.mensagem, {
            position: 'top-right',
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    async handleExport() {
      this.$refs.exportModal.open()
    },
    async submitExport(e) {
      this.exportLoading = true

      const params = {
        format: e.format,
        id_aluno: this.aluno,
        id_instrutor: this.instrutor,
        id_veiculo: this.veiculo,
        status: this.status,
        dateI: this.initialDate,
        dateF: this.finalDate,
      }

      await this.$http
        .get(`${process.env.VUE_APP_URL_API_V2}/aulas/praticas/relatorio/praticas`, {
          params,
        })
        .then(response => {
          if (e.format === 'pdf') {
            _export.downloadPDF(response.body.dados, 'aulas-praticas')
          } else {
            _export.downloadCSV(response.body.dados, 'aulas-praticas')
          }
        })
        .catch(err => {
          this.$toasted.show(err.body.mensagem, {
            position: 'top-right',
          })
          this.$toasted.show('Erro ao exportar aulas práticas.', {
            position: 'top-right',
          })
        })
        .finally(() => {
          this.exportLoading = false
        })

      this.$refs.exportModal.close()
    },
    justificativaModal(idAulaPratica) {
      this.$refs.justificativaModal.open()
      this.justificativaAulaPratica = idAulaPratica

      if (this.justificativa != '') {
        this.justificativaVazia = false
      }
    },
    detalhesAulasPraticas(idAulaPratica) {
      this.aulaId = idAulaPratica
      this.$refs.modalRealizadas.open()
      this.modal.show = false
      this.modal.load = true
      this.modal.conteudo = {}

      AgendamentoController.getAulaRealizadaById(idAulaPratica, response => {
        // console.log(response.data.dados);
        this.modal.conteudo = response.data.dados
        if (this.modal.conteudo.anotacao.edu_content) {
          var url =
            urlApi + `/planos/praticos/constante/${this.modal.conteudo.anotacao.edu_content}`
          this.$http.get(url).then(function (response) {
            this.modal.conteudo.anotacao.edu_content = response.data
            this.modal.load = false
            this.modal.show = true
            setTimeout(function () {
              this.checkImage(0)
            }, 1000)
          })
        } else {
          this.modal.conteudo.anotacao.edu_content = '-'
          this.modal.load = false
          this.modal.show = true
          setTimeout(function () {
            this.checkImage(0)
          }, 1000)
        }
      })
    },
    editSave() {
      let self = this
      let formEdit = Serialize.withFormData('form_editar_aula_pratica')
      self.$http
        .post(urlApi + '/agendamentos/editar/' + self.aulaEdit.id_aulas_praticas, formEdit)
        .then(function (response) {
          this.$refs.editModal.close()
          self.loadData()
        })
        .catch(function (error) {
          AgendamentoController.launchToast('fa-times', error.data.dados)
        })
    },
    async removerAula(idAulaPratica, justificativa) {
      await this.$http
        .get(
          urlApi +
            `/agendamentos/deletar/${idAulaPratica}?justificativa=${justificativa.toString()}`
        )
        .then(async function (response) {
          this.aulas = this.aulas.filter(
            data => data.id_aulas_praticas !== response.data.dados.id_aulas_praticas
          )
          this.$refs.justificativaModal.close()
          this.justificativa = ''

          this.fetchAulas()
        })
        .catch(function (response) {
          AgendamentoController.launchToast('fa-times', response.data.dados)
          this.justificativa = ''
        })
    },
    detalhesAulaPraticaAgendada(idAulaPratica) {
      this.$refs.smallModal.open()
      this.modalAulaPraticaAgendada.load = true
      this.modalAulaPraticaAgendada.conteudo = {}

      AgendamentoController.getAulaAgendadaById(idAulaPratica, response => {
        // console.log(response);
        this.modalAulaPraticaAgendada.conteudo = response.data.dados
        this.modalAulaPraticaAgendada.load = false
      })
    },
    ativar(opcao) {
      if (opcao == 'Avaliações') {
        this.avaliacoes = true
      }
      if (opcao == 'Feedback') {
        this.feedback = true
      }
    },
    openInfoModal(aulaId) {
      this.$refs.infoModal.open(aulaId)
    },
    detalhesAulaCancelada(idAulaPratica) {
      this.$refs.smallModal.open()
      this.modalAulaPraticaAgendada.load = true
      this.modalAulaPraticaAgendada.conteudo = {}

      AgendamentoController.getAulaAgendadaById(idAulaPratica, response => {
        this.modalAulaPraticaAgendada.conteudo = response.data.dados
        this.modalAulaPraticaAgendada.load = false
      })
    },
    
    abrirModalJustificativa(justificativa) {
      this.justificativaCompleta = justificativa || '-';
      if (this.$refs.modalJustificativaCompleta) {
        this.$refs.modalJustificativaCompleta.open();
      }
    },
  },
  watch: {
    justificativa(newJustificativa, oldJustificativa) {
      if (newJustificativa.length > 0) {
        this.justificativaVazia = false
      } else {
        this.justificativaVazia = true
      }
    },
    // Limpar seleções quando mudar de página ou fazer nova consulta
    aulas() {
      this.aulasSelecionadas = [];
    }
  },
}
</script>

<style lang="scss" scoped>
.aulas-container {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;

  .form-container {
    display: flex;
    flex-direction: column;
    gap: 2.5rem;

    .inputs-container {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;

      .inputs-row {
        display: flex;
        flex-wrap: wrap;
        gap: 1.5rem;

        .input-full {
          flex: 1;
        }

        .input-status {
          max-width: 25.75rem;
        }
      }
    }

    .form-buttons {
      align-self: flex-end;

      display: flex;
      gap: 0.5rem;
    }
  }

  .table-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .pagination-container {
      align-self: flex-end;
    }
  }

  .actions {
    display: flex;
    align-items: center;

    div {
      padding: 0 0.5rem;
      display: flex;
      justify-content: center;

      &:not(:first-child) {
        border-left: 1px solid #ececec;
      }

      &:not(:last-child) {
        border-right: 1px solid #ececec;
      }
    }
  }

  /* Animações */
  .slide-down-enter-active, .slide-down-leave-active {
    transition: all 0.3s ease;
  }
  .slide-down-enter, .slide-down-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }

  /* Barra de ações melhorada */
  .bulk-actions-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    margin-bottom: 1rem;
    background: #1C2D5A;
    border-radius: 8px;
    color: white;
    box-shadow: 0 2px 8px rgba(28, 45, 90, 0.15);
    border-left: 4px solid #FFC107;

    .selection-info {
      display: flex;
      align-items: center;
      gap: 0.75rem;

      .selection-icon {
        padding: 0.5rem;
        background: #FFC107;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #1C2D5A;
      }

      .selection-text {
        display: flex;
        flex-direction: column;

        .count {
          font-size: 1.25rem;
          font-weight: 700;
          line-height: 1;
          color: #FFC107;
        }

        .label {
          font-size: 0.875rem;
          opacity: 0.9;
          color: #ffffff;
        }
      }
    }

    .bulk-actions {
      display: flex;
      gap: 0.5rem;
    }
  }

  .selected {
    background-color: #e3f2fd !important;
    border-left: 4px solid #2196f3;
  }

  /* Estilos do modal melhorados */
  .modal-title-custom {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem;
    border-bottom: 1px solid #e9ecef;

    .title-icon {
      padding: 0.75rem;
      background: #fee2e2;
      border-radius: 12px;
      color: #dc2626;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .title-text {
      h3 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 600;
        color: #1f2937;
      }

      p {
        margin: 0.25rem 0 0 0;
        font-size: 0.875rem;
        color: #6b7280;
      }
    }
  }

  .modal-content-custom {
    padding: 1.5rem;
  }

  .selected-info-card {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 1rem;
    margin-bottom: 1.5rem;

    .info-header {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 1rem;

      .info-icon {
        padding: 0.5rem;
        background: #dbeafe;
        border-radius: 8px;
        color: #2563eb;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .info-text {
        display: flex;
        flex-direction: column;

        .count {
          font-size: 1.125rem;
          font-weight: 600;
          color: #1f2937;
        }

        .label {
          font-size: 0.875rem;
          color: #6b7280;
        }
      }
    }

    .aulas-preview {
      max-height: 200px;
      overflow-y: auto;

      .aula-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.75rem;
        background: white;
        border-radius: 8px;
        margin-bottom: 0.5rem;
        border: 1px solid #e5e7eb;

        &:last-child {
          margin-bottom: 0;
        }

        .aula-info {
          display: flex;
          flex-direction: column;

          .aluno-nome {
            font-weight: 500;
            color: #1f2937;
          }

          .aula-data {
            font-size: 0.875rem;
            color: #6b7280;
          }
        }

        .aula-status {
          .status-badge {
            padding: 0.25rem 0.75rem;
            background: #fef3c7;
            color: #d97706;
            border-radius: 6px;
            font-size: 0.75rem;
            font-weight: 500;
          }
        }
      }
    }
  }

  .form-section {
    .form-label {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;

      .label-text {
        font-weight: 600;
        color: #1f2937;
      }

      .label-required {
        font-size: 0.75rem;
        color: #dc2626;
        background: #fee2e2;
        padding: 0.25rem 0.5rem;
        border-radius: 4px;
      }
    }

    .textarea-container {
      position: relative;

      .form-textarea {
        width: 100%;
        border: 2px solid #e5e7eb;
        border-radius: 8px;
        padding: 0.75rem;
        font-family: inherit;
        font-size: 0.875rem;
        resize: vertical;
        min-height: 100px;
        transition: border-color 0.2s ease;

        &:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        &:disabled {
          background-color: #f9fafb;
          cursor: not-allowed;
        }
      }

      .textarea-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 0.5rem;

        .char-count {
          font-size: 0.75rem;
          color: #6b7280;

          &.limit-reached {
            color: #dc2626;
            font-weight: 500;
          }
        }

        .validation-icon {
          .valid-icon {
            color: #10b981;
            font-weight: bold;
          }
        }
      }
    }

    .form-hint {
      font-size: 0.75rem;
      color: #6b7280;
      margin-top: 0.5rem;
    }
  }

  .modal-actions-custom {
    padding: 1rem 1.5rem;
    border-top: 1px solid #e5e7eb;
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    background: #f9fafb;
  }

  /* Animação do spinner */
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  /* Estilo específico para o modal de cancelamento em lote */
  [data-testid="modal_cancelamento_lote"] {
    .modal-dialog {
      height: 100vh !important;
      max-height: 100vh !important;
      margin: 0 !important;
    }
    
    .modal-content {
      height: 100% !important;
      border-radius: 0 !important;
    }
  }

  /* Estilo específico para o modal de justificativa */
  [data-testid="modal_justificativa_completa"] {
    .modal-dialog {
      max-width: 650px !important;
      width: 95% !important;
      margin: 30px auto !important;
    }
    
    .modal-content {
      border-radius: 12px !important;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15) !important;
    }
  }

  /* Estilo para tabela customizada com checkboxes */
  .table-container {
    .vuestic-table {
      width: 100%;
      border-collapse: collapse;
      background: white;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

      thead {
        background: #f8fafc;
        
        th {
          padding: 12px 16px;
          text-align: left;
          font-weight: 600;
          color: #374151;
          border-bottom: 1px solid #e5e7eb;
          font-size: 0.875rem;
        }
      }

      tbody {
        tr {
          border-bottom: 1px solid #f3f4f6;
          transition: background-color 0.2s ease;

          &:hover {
            background: #f9fafb;
          }

          &.selected {
            background: #e0f2fe;
          }

          td {
            padding: 12px 16px;
            font-size: 0.875rem;
            color: #374151;

            &.checkbox {
              width: 40px;
              text-align: center;
              
              label {
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                
                input[type="checkbox"] {
                  margin: 0;
                  cursor: pointer;
                }
                
                span {
                  display: none;
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
